import { TeamInviteForm } from "@/components/TeamInviteForm";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface TeamInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  /** The Channel Partner organisation name shown in the joining copy. */
  orgName: string | null;
  /** The organisation's id, surfaced next to the name (Flow A D1). */
  orgId?: string | null;
}

/**
 * po-dashboard (Insights & Admin Flow A): the shared <TeamInviteForm> wrapped
 * in a right-hand Sheet (CHP-792) so a PO can invite team members without
 * leaving the page. Two launch points use it — the Insights & Admin header CTA
 * and the partner dashboard "Invite new team member" button (CHP-788). The
 * standalone /dashboard/partner/team route and the onboarding Registration
 * Complete page keep rendering the form inline (those flows redirect to the
 * dashboard on success); here the form's onDone closes the Sheet instead.
 */
export function TeamInviteDialog({
  open,
  onOpenChange,
  token,
  orgName,
  orgId,
}: TeamInviteDialogProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-md"
        // CHP-800: the Access-level <Select> renders in a portal outside the
        // sheet, so dismissing it (or choosing an option) registers as an
        // interact-outside and would otherwise close the whole panel. The Sheet
        // uses the same Radix dialog primitive, so the guard carries over: keep
        // the panel open when the interaction originates from the Select popper.
        onInteractOutside={(e) => {
          const target = e.target as Element | null;
          if (target?.closest("[data-radix-popper-content-wrapper],[data-radix-select-viewport]")) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader>
          <SheetTitle>Invite your Team</SheetTitle>
          <SheetDescription>Invite team members as Program Owners or BDMs.</SheetDescription>
        </SheetHeader>
        <TeamInviteForm
          token={token}
          orgName={orgName}
          orgId={orgId}
          onDone={() => onOpenChange(false)}
        />
      </SheetContent>
    </Sheet>
  );
}
